/**
 * OnboardingWizard — questionario obbligatorio al primo accesso ("SQ LABS – ELIOS").
 *
 * Raccoglie le risposte (azienda, personalità, informazioni, trasferimenti,
 * voce + un blocco per ogni canale attivo) e le invia a
 * `POST /elios-client/onboarding/submit`, che genera con l'AI il prompt di ogni
 * agente e lo applica (ElevenLabs per inbound/outbound, ai_prompt per WhatsApp,
 * auto_followup_prompt_template per il follow-up).
 *
 * È renderizzato a tutto schermo da EliosLayout AL POSTO della normale UI finché
 * l'onboarding non è completato.
 *
 * Props:
 *   requiredChannels: string[]  // sottoinsieme di ['inbound','outbound','whatsapp','followup']
 *   companyName: string
 *   initialAnswers: object       // bozza salvata in precedenza
 *   onDone: () => void           // chiamata a completamento → sblocca l'app
 */
import { useMemo, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Loader2, Sparkles, Check, ChevronLeft, ChevronRight, Plus, Trash2, ClipboardList } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

const CHANNEL_META = {
  inbound:  { label: 'Inbound (chiamate in entrata)',  voice: true },
  outbound: { label: 'Outbound (chiamate in uscita)',  voice: true },
  whatsapp: { label: 'WhatsApp',                        voice: false },
  followup: { label: 'Follow-up (richiami)',           voice: false },
};

// Hoisted so it keeps a stable component identity across renders — defining it
// inside OnboardingWizard would remount every input on each keystroke (focus loss).
const Field = ({ label, hint, children }) => (
  <div className="space-y-1">
    <label className="text-sm font-semibold text-gray-700">{label}</label>
    {hint && <p className="text-xs text-gray-400">{hint}</p>}
    {children}
  </div>
);

const emptyAnswers = (companyName) => ({
  company: { name: companyName || '', sector: '', website: '', area: '', hours: '', differentiator: '' },
  personality: { adjectives: '', register: 'lei', languages: '', presentation: '' },
  info: { faq: [{ q: '', a: '' }], services: '', prices_allowed: false, prices_note: '', confidential: '', data_to_collect: [{ field: '', required: false }] },
  transfers: { rules: '', before_transfer: '', on_no_answer: '', software: '' },
  voice: { gender: '', age_impression: '' },
  channels: {},
});

export default function OnboardingWizard({ requiredChannels = [], companyName = '', initialAnswers = null, onDone }) {
  const [answers, setAnswers] = useState(() => {
    const base = emptyAnswers(companyName);
    if (initialAnswers && Object.keys(initialAnswers).length) {
      return {
        ...base,
        ...initialAnswers,
        company: { ...base.company, ...(initialAnswers.company || {}) },
        personality: { ...base.personality, ...(initialAnswers.personality || {}) },
        info: { ...base.info, ...(initialAnswers.info || {}) },
        transfers: { ...base.transfers, ...(initialAnswers.transfers || {}) },
        voice: { ...base.voice, ...(initialAnswers.voice || {}) },
        channels: { ...(initialAnswers.channels || {}) },
      };
    }
    return base;
  });
  const [submitting, setSubmitting] = useState(false);

  const hasVoice = requiredChannels.some(c => CHANNEL_META[c]?.voice);

  // Build the ordered list of steps based on active channels.
  const steps = useMemo(() => {
    const s = ['company', 'personality', 'info', 'transfers'];
    if (hasVoice) s.push('voice');
    requiredChannels.forEach(c => s.push(`channel:${c}`));
    s.push('generate');
    return s;
  }, [requiredChannels, hasVoice]);

  const [stepIdx, setStepIdx] = useState(0);
  const step = steps[stepIdx];
  const isLast = stepIdx === steps.length - 1;

  // ── helpers to update nested answers ──────────────────────────────────────
  const setSection = (section, patch) =>
    setAnswers(prev => ({ ...prev, [section]: { ...prev[section], ...patch } }));
  const setChannel = (ch, patch) =>
    setAnswers(prev => ({ ...prev, channels: { ...prev.channels, [ch]: { ...(prev.channels[ch] || {}), ...patch } } }));

  const updateFaq = (i, key, val) => setAnswers(prev => {
    const faq = [...(prev.info.faq || [])];
    faq[i] = { ...faq[i], [key]: val };
    return { ...prev, info: { ...prev.info, faq } };
  });
  const addFaq = () => setAnswers(prev => ({ ...prev, info: { ...prev.info, faq: [...(prev.info.faq || []), { q: '', a: '' }] } }));
  const removeFaq = (i) => setAnswers(prev => ({ ...prev, info: { ...prev.info, faq: prev.info.faq.filter((_, j) => j !== i) } }));

  const updateData = (i, key, val) => setAnswers(prev => {
    const d = [...(prev.info.data_to_collect || [])];
    d[i] = { ...d[i], [key]: val };
    return { ...prev, info: { ...prev.info, data_to_collect: d } };
  });
  const addData = () => setAnswers(prev => ({ ...prev, info: { ...prev.info, data_to_collect: [...(prev.info.data_to_collect || []), { field: '', required: false }] } }));
  const removeData = (i) => setAnswers(prev => ({ ...prev, info: { ...prev.info, data_to_collect: prev.info.data_to_collect.filter((_, j) => j !== i) } }));

  // ── validation (minimal) ──────────────────────────────────────────────────
  const canProceed = () => {
    if (step === 'company') return !!answers.company.name.trim();
    if (step.startsWith('channel:')) {
      const ch = step.split(':')[1];
      return !!(answers.channels[ch]?.purpose || '').trim();
    }
    return true;
  };

  const next = () => {
    if (!canProceed()) { toast.warning('Compila i campi obbligatori per continuare'); return; }
    setStepIdx(i => Math.min(i + 1, steps.length - 1));
  };
  const prev = () => setStepIdx(i => Math.max(i - 1, 0));

  const submit = async () => {
    setSubmitting(true);
    try {
      const r = await api.post('/elios-client/onboarding/submit', { answers });
      if (r.data.completed) {
        toast.success(r.data.message || 'Agenti configurati e attivati', { duration: 6000 });
        onDone?.();
      } else {
        const failed = Object.keys(r.data.errors || {});
        toast.error(
          failed.length
            ? `Alcuni canali non sono stati configurati: ${failed.join(', ')}. Riprova.`
            : (r.data.message || 'Configurazione non completata'),
          { duration: 8000 }
        );
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore durante la generazione dei prompt');
    } finally {
      setSubmitting(false);
    }
  };

  const stepTitle = {
    company: '1. La tua azienda',
    personality: '2. Personalità e tono',
    info: '3. Informazioni e dati',
    transfers: '4. Trasferimenti e strumenti',
    voice: '5. Voce dell\'agente',
    generate: 'Genera e attiva gli agenti',
  };
  const title = step.startsWith('channel:')
    ? `Agente: ${CHANNEL_META[step.split(':')[1]]?.label || step.split(':')[1]}`
    : stepTitle[step];

  return (
    <div className="fixed inset-0 z-[60] bg-[#0A0F1C] overflow-y-auto">
      <div className="min-h-full flex items-start justify-center p-4 sm:p-8">
        <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl my-4">
          {/* Header */}
          <div className="px-6 pt-6 pb-4 border-b">
            <div className="flex items-center gap-2 text-cyan-600">
              <ClipboardList className="w-5 h-5" />
              <span className="text-xs font-bold uppercase tracking-wide">Configurazione iniziale</span>
            </div>
            <h1 className="text-xl font-bold text-gray-900 mt-1">{title}</h1>
            {/* Progress */}
            <div className="mt-3 flex items-center gap-1.5">
              {steps.map((s, i) => (
                <div key={s} className={`h-1.5 flex-1 rounded-full ${i <= stepIdx ? 'bg-cyan-500' : 'bg-gray-200'}`} />
              ))}
            </div>
            <p className="text-[11px] text-gray-400 mt-1.5">Passo {stepIdx + 1} di {steps.length} · Più dettagli dai, più preciso sarà l'agente. Lascia vuoto ciò che non è pertinente.</p>
          </div>

          {/* Body */}
          <div className="px-6 py-5 space-y-4">
            {step === 'company' && (
              <>
                <Field label="Nome azienda *">
                  <Input value={answers.company.name} onChange={e => setSection('company', { name: e.target.value })} placeholder="Es. Casa in Appennino" />
                </Field>
                <Field label="Settore di attività">
                  <Input value={answers.company.sector} onChange={e => setSection('company', { sector: e.target.value })} placeholder="Es. agenzia immobiliare" />
                </Field>
                <Field label="Sito web / social">
                  <Input value={answers.company.website} onChange={e => setSection('company', { website: e.target.value })} placeholder="www.esempio.it" />
                </Field>
                <Field label="Zona o città in cui operate">
                  <Input value={answers.company.area} onChange={e => setSection('company', { area: e.target.value })} placeholder="Es. Modena e provincia" />
                </Field>
                <Field label="Orari di apertura / disponibilità">
                  <Textarea rows={2} value={answers.company.hours} onChange={e => setSection('company', { hours: e.target.value })} placeholder="Es. Lun 15–19, Mar–Ven 9–12.30 e 15.30–19" />
                </Field>
                <Field label="Cosa vi rende diversi dai concorrenti?">
                  <Textarea rows={2} value={answers.company.differentiator} onChange={e => setSection('company', { differentiator: e.target.value })} placeholder="In una frase" />
                </Field>
              </>
            )}

            {step === 'personality' && (
              <>
                <Field label="Carattere ideale dell'agente" hint="Max 3-4 aggettivi (es. cordiale, professionale, sintetico)">
                  <Input value={answers.personality.adjectives} onChange={e => setSection('personality', { adjectives: e.target.value })} placeholder="cordiale, sintetico" />
                </Field>
                <Field label="Registro con gli interlocutori">
                  <div className="flex flex-wrap gap-2">
                    {[['lei', 'Dare del Lei'], ['tu', 'Dare del tu'], ['adattivo', 'Adattarsi all\'interlocutore']].map(([val, lbl]) => (
                      <button key={val} type="button" onClick={() => setSection('personality', { register: val })}
                        className={`px-3 py-1.5 rounded-lg border text-sm ${answers.personality.register === val ? 'border-cyan-400 bg-cyan-50 text-cyan-700' : 'border-gray-200 text-gray-600'}`}>
                        {lbl}
                      </button>
                    ))}
                  </div>
                </Field>
                <Field label="Lingue in cui deve rispondere">
                  <Input value={answers.personality.languages} onChange={e => setSection('personality', { languages: e.target.value })} placeholder="Es. italiano e inglese (o: tutte)" />
                </Field>
                <Field label="Come si presenta / messaggio di apertura" hint="Es. «Sono l'assistente vocale di …, come posso esserle utile?»">
                  <Textarea rows={2} value={answers.personality.presentation} onChange={e => setSection('personality', { presentation: e.target.value })} />
                </Field>
              </>
            )}

            {step === 'info' && (
              <>
                <Field label="Domande frequenti e risposte corrette">
                  <div className="space-y-2">
                    {(answers.info.faq || []).map((f, i) => (
                      <div key={i} className="rounded-lg border border-gray-200 p-2 space-y-1.5">
                        <Input value={f.q} onChange={e => updateFaq(i, 'q', e.target.value)} placeholder="Domanda del cliente" />
                        <div className="flex gap-2">
                          <Textarea rows={2} value={f.a} onChange={e => updateFaq(i, 'a', e.target.value)} placeholder="Risposta dell'agente" />
                          <button type="button" onClick={() => removeFaq(i)} className="text-gray-400 hover:text-red-500 shrink-0"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </div>
                    ))}
                    <Button type="button" variant="outline" size="sm" onClick={addFaq}><Plus className="w-3.5 h-3.5 mr-1" /> Aggiungi FAQ</Button>
                  </div>
                </Field>
                <Field label="Prodotti / servizi da saper descrivere">
                  <Textarea rows={2} value={answers.info.services} onChange={e => setSection('info', { services: e.target.value })} placeholder="Separati da virgola" />
                </Field>
                <Field label="Si può parlare di prezzi?">
                  <div className="flex items-center gap-2">
                    <label className="flex items-center gap-1.5 text-sm">
                      <input type="checkbox" checked={!!answers.info.prices_allowed} onChange={e => setSection('info', { prices_allowed: e.target.checked })} className="accent-cyan-500" />
                      Sì, l'agente può comunicare prezzi
                    </label>
                  </div>
                  {answers.info.prices_allowed && (
                    <Textarea rows={2} value={answers.info.prices_note} onChange={e => setSection('info', { prices_note: e.target.value })} placeholder="Quali e come" className="mt-2" />
                  )}
                </Field>
                <Field label="Informazioni riservate da NON divulgare mai">
                  <Textarea rows={2} value={answers.info.confidential} onChange={e => setSection('info', { confidential: e.target.value })} placeholder="Es. note riservate, indirizzi precisi, provvigioni" />
                </Field>
                <Field label="Dati da raccogliere dall'interlocutore">
                  <div className="space-y-2">
                    {(answers.info.data_to_collect || []).map((d, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <Input value={d.field} onChange={e => updateData(i, 'field', e.target.value)} placeholder="Es. Nome e cognome" />
                        <label className="flex items-center gap-1 text-xs text-gray-600 shrink-0">
                          <input type="checkbox" checked={!!d.required} onChange={e => updateData(i, 'required', e.target.checked)} className="accent-cyan-500" />
                          Obbl.
                        </label>
                        <button type="button" onClick={() => removeData(i)} className="text-gray-400 hover:text-red-500 shrink-0"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    ))}
                    <Button type="button" variant="outline" size="sm" onClick={addData}><Plus className="w-3.5 h-3.5 mr-1" /> Aggiungi dato</Button>
                  </div>
                </Field>
              </>
            )}

            {step === 'transfers' && (
              <>
                <Field label="A chi vanno passate le chiamate/conversazioni?" hint="Es. «chi chiede di Brunella → Brunella», «zona Appennino → Stefano»">
                  <Textarea rows={3} value={answers.transfers.rules} onChange={e => setSection('transfers', { rules: e.target.value })} />
                </Field>
                <Field label="Dati da raccogliere prima di trasferire">
                  <Textarea rows={2} value={answers.transfers.before_transfer} onChange={e => setSection('transfers', { before_transfer: e.target.value })} placeholder="Es. nome, cognome e motivo" />
                </Field>
                <Field label="Cosa fare se il collega non risponde">
                  <Textarea rows={2} value={answers.transfers.on_no_answer} onChange={e => setSection('transfers', { on_no_answer: e.target.value })} placeholder="Es. dopo 5 squilli riprendere e promettere richiamo" />
                </Field>
                <Field label="Software gestionali / CRM usati">
                  <Input value={answers.transfers.software} onChange={e => setSection('transfers', { software: e.target.value })} placeholder="Es. Agestanet, OnOffice" />
                </Field>
              </>
            )}

            {step === 'voice' && (
              <>
                <Field label="Voce preferita">
                  <div className="flex flex-wrap gap-2">
                    {[['femminile', 'Femminile'], ['maschile', 'Maschile'], ['indifferente', 'Indifferente']].map(([val, lbl]) => (
                      <button key={val} type="button" onClick={() => setSection('voice', { gender: val })}
                        className={`px-3 py-1.5 rounded-lg border text-sm ${answers.voice.gender === val ? 'border-cyan-400 bg-cyan-50 text-cyan-700' : 'border-gray-200 text-gray-600'}`}>
                        {lbl}
                      </button>
                    ))}
                  </div>
                </Field>
                <Field label="Età / impressione desiderata" hint="Es. giovane e dinamica; matura e autorevole; calda e accogliente">
                  <Input value={answers.voice.age_impression} onChange={e => setSection('voice', { age_impression: e.target.value })} />
                </Field>
              </>
            )}

            {step.startsWith('channel:') && (() => {
              const ch = step.split(':')[1];
              const meta = CHANNEL_META[ch] || {};
              const data = answers.channels[ch] || {};
              return (
                <>
                  <p className="text-sm text-gray-500">Definisci come deve comportarsi l'agente <strong>{meta.label}</strong>.</p>
                  <Field label="Compito principale *" hint="La cosa più importante che deve fare">
                    <Textarea rows={2} value={data.purpose || ''} onChange={e => setChannel(ch, { purpose: e.target.value })} />
                  </Field>
                  <Field label="Altri compiti da gestire">
                    <Textarea rows={2} value={data.tasks || ''} onChange={e => setChannel(ch, { tasks: e.target.value })} placeholder="Es. smistare chiamate, raccogliere lead, qualificare la richiesta" />
                  </Field>
                  <Field label="Cosa NON deve fare mai">
                    <Textarea rows={2} value={data.never_do || ''} onChange={e => setChannel(ch, { never_do: e.target.value })} placeholder="Es. non proporre immobili né appuntamenti" />
                  </Field>
                  {!meta.voice && (
                    <Field label={`Istruzioni specifiche per ${meta.label}`} hint={ch === 'whatsapp' ? 'Tono e regole per i messaggi WhatsApp (testo breve).' : 'Come impostare i richiami: cosa ricordare, quando proporre i passi successivi.'}>
                      <Textarea rows={3} value={data.instructions || ''} onChange={e => setChannel(ch, { instructions: e.target.value })} />
                    </Field>
                  )}
                </>
              );
            })()}

            {step === 'generate' && (
              <div className="text-center py-6 space-y-3">
                <Sparkles className="w-10 h-10 text-cyan-500 mx-auto" />
                <p className="text-gray-700 font-semibold">Tutto pronto!</p>
                <p className="text-sm text-gray-500 max-w-md mx-auto">
                  Genereremo con l'AI il prompt di ogni agente attivo
                  ({requiredChannels.map(c => CHANNEL_META[c]?.label || c).join(', ')}) e lo attiveremo.
                  L'operazione richiede qualche secondo.
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t flex items-center justify-between">
            <Button variant="outline" onClick={prev} disabled={stepIdx === 0 || submitting}>
              <ChevronLeft className="w-4 h-4 mr-1" /> Indietro
            </Button>
            {isLast ? (
              <Button onClick={submit} disabled={submitting} className="bg-cyan-500 hover:bg-cyan-600">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Sparkles className="w-4 h-4 mr-1" />}
                Genera e attiva gli agenti
              </Button>
            ) : (
              <Button onClick={next} disabled={submitting} className="bg-cyan-500 hover:bg-cyan-600">
                Avanti <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
